import { formatMoney } from '@theme/utilities';
import { ThemeEvents, CartUpdatedEvent, FormUpdatedEvent } from '@theme/events';

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.addToCartButton = null;
    this.addToCartButtonContainer = null;
    this.addToCartBehavior = null;
    this.toggleCrossSells = null;
    this.quantityBreaks = null;
    this.subscription = null;
    this.quantitySelectorInput = null;

    // Handlers bindés pour pouvoir les retirer correctement
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onVariantSelected = this.onVariantSelected.bind(this);
    this.onFormUpdated = this.onFormUpdated.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form[action$="/cart/add"]');
    this.addToCartButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.addToCartButtonContainer = this.querySelector('[data-ref="add-to-cart-button-container"]');
    this.addToCartBehavior = document.querySelector('cart-icon')?.addToCartBehavior || 'open_cart';
    this.toggleCrossSells = this.querySelector('[data-ref="toggle-cross-sells"]');
    this.quantityBreaks = this.querySelector('[data-ref="quantity-breaks"]');
    this.subscription = this.querySelector('[data-ref="subscription"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="product-quantity-selector"] input[name="quantity"]');

    if (!this.form || !this.addToCartButton || !this.addToCartButtonContainer) return;

    this.form.addEventListener('submit', this.handleSubmit);
    this.addEventListener(ThemeEvents.variantSelected, this.onVariantSelected);
    this.addEventListener(ThemeEvents.variantUpdated, this.onFormUpdated);
    document.addEventListener(ThemeEvents.toggleCrossSellsUpdated, this.onFormUpdated);
    document.addEventListener(ThemeEvents.quantityBreaksUpdated, this.onFormUpdated);
    document.addEventListener(ThemeEvents.subscriptionUpdated, this.onFormUpdated);
    this.quantitySelectorInput?.addEventListener('change', this.onFormUpdated);

    /**
     * Quand le formulaire a finit de charger, on déclenche une mise à jour de prix pour éviter les problèmes de prix
     * si la quantité par défaut n'est pas 1 ou que l'abonnement est pré-sélectionné
     */
    document.addEventListener('DOMContentLoaded', () => this.onFormUpdated(), { once: true });
    this.addEventListener('quickAddContentLoaded', () => this.onFormUpdated(), { once: true });
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.removeEventListener(ThemeEvents.variantSelected, this.onVariantSelected);
    this.removeEventListener(ThemeEvents.variantUpdated, this.onFormUpdated);
    document.removeEventListener(ThemeEvents.toggleCrossSellsUpdated, this.onFormUpdated);
    document.removeEventListener(ThemeEvents.quantityBreaksUpdated, this.onFormUpdated);
    document.removeEventListener(ThemeEvents.subscriptionUpdated, this.onFormUpdated);
    this.quantitySelectorInput?.removeEventListener('change', this.onFormUpdated);
  }

  onVariantSelected() {
    if (this.addToCartButton) this.addToCartButton.disabled = true;
  }

  onFormUpdated(event) {
    // On récupère le DOM actuel du bouton d'ajout au panier
    const addToCartButtonContainer = this.addToCartButtonContainer;

    // Le event.detail est null si l'évenement de mise à jour est déclenché par les cross-sells à activer et non par les variantes, dans ce cas là on utilise le DOM actuel
    let newAddToCartButtonContainer = addToCartButtonContainer;
    if (event && event.detail != null) {
      newAddToCartButtonContainer = event.detail.data.html.querySelector('[data-ref="add-to-cart-button-container"]');
    }

    if (!newAddToCartButtonContainer || !addToCartButtonContainer) return;

    // On remplace le DOM actuel par le nouveau DOM
    if (addToCartButtonContainer !== newAddToCartButtonContainer) {
      addToCartButtonContainer.replaceWith(newAddToCartButtonContainer);
    }

    // On réassigne les nouvelles valeurs du bouton pour le reste du fonctionnement du composant
    this.addToCartButton = newAddToCartButtonContainer.querySelector('[data-ref="add-to-cart-button"]');
    this.addToCartButtonContainer = newAddToCartButtonContainer;

    // On récupère les prix totaux du formulaire (incluant le produit de base, sa quantité, les cross-sells à activer, etc.)
    const newTotalPrice = this.getCurrentTotalPrice().price;
    const newTotalCompareAtPrice = this.getCurrentTotalPrice().compare_at_price;

    // On met à jour les prix dans le nouveau DOM à insérer
    const priceElement = this.addToCartButtonContainer.querySelector('[data-ref="price"]');
    if (priceElement != null) {
      priceElement.innerHTML = formatMoney(newTotalPrice);
    }

    const compareAtPriceElement = this.addToCartButtonContainer.querySelector('[data-ref="compare-at-price"]');
    if (compareAtPriceElement != null) {
      compareAtPriceElement.innerHTML = '';
      if (newTotalCompareAtPrice > newTotalPrice) {
        compareAtPriceElement.innerHTML = formatMoney(newTotalCompareAtPrice);
      }
    }

    this.dispatchEvent(new FormUpdatedEvent(this.getCurrentTotalPrice()));
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (!this.form || !this.addToCartButton) return;

    this.addToCartButton.classList.add('is-loading');
    const backendOrderNote = this.#buildBackendOrderNote();

    try {
      let items = [];
      const itemInputs = Array.from(
        this.form.querySelectorAll(
          'input[name^="items["], select[name^="items["], textarea[name^="items["]',
        ),
      ).filter((input) => !input.disabled);

      if (itemInputs.length > 0) {
        const itemsData = {};

        itemInputs.forEach((input) => {
          const indexMatch = input.name.match(/items\[(\d+)\]/);
          if (!indexMatch) return;

          const index = indexMatch[1];
          if (!itemsData[index]) itemsData[index] = {};

          if (input.name.includes('[id]')) {
            itemsData[index].id = input.value;
          } else if (input.name.includes('[quantity]')) {
            itemsData[index].quantity = parseInt(input.value, 10) || 1;
          } else if (input.name.includes('[selling_plan]') && input.value) {
            itemsData[index].selling_plan = input.value;
          } else if (input.name.includes('[properties]')) {
            const propertyMatch = input.name.match(/\[properties\]\[([^\]]+)\]/);
            if (propertyMatch && input.value !== '') {
              if (!itemsData[index].properties) itemsData[index].properties = {};
              itemsData[index].properties[propertyMatch[1]] = input.value;
            }
          }
        });

        items.push(...Object.values(itemsData));
      }

      if (this.quantityBreaks?.isActive?.()) {
        const mainItems = this.quantityBreaks.getSelectedMainItems?.()
          || this.quantityBreaks.getSelectedItems?.()
          || [];
        const flocageBundle = this.querySelector('flocage-bundle');

        mainItems.forEach((item, index) => {
          const bundlePosition = String(index + 1);
          const groupProperties =
            flocageBundle && typeof flocageBundle.getGroupProperties === 'function'
              ? flocageBundle.getGroupProperties(index + 1)
              : {};

          item.properties = {
            ...(item.properties || {}),
            ...groupProperties,
            '_Concept0ry bundle position': bundlePosition,
          };
        });

        const sellingPlanInput = this.form.querySelector(
          'input[name="selling_plan"], select[name="selling_plan"]',
        );
        if (sellingPlanInput?.value) {
          mainItems.forEach((item) => {
            item.selling_plan = sellingPlanInput.value;
          });
        }

        items.push(...mainItems);

        const quantityBreakCrossSells = this.quantityBreaks
          .getSelectedQuantity?.()
          ?.getCrossSellItems?.() || [];
        items.push(...quantityBreakCrossSells);
      } else {
        const properties = {};

        this.form
          .querySelectorAll(
            'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]',
          )
          .forEach((input) => {
            if (input.disabled || input.value === '') return;
            const propertyKey = input.name.replace('properties[', '').replace(']', '');
            properties[propertyKey] = input.value;
          });

        const variantInput = this.form.querySelector('input[name="id"]');
        if (!variantInput?.value) throw new Error('Aucune variante sélectionnée.');

        const item = {
          id: variantInput.value,
          quantity: parseInt(this.form.querySelector('input[name="quantity"]')?.value, 10) || 1,
        };

        if (Object.keys(properties).length > 0) item.properties = properties;

        const sellingPlanInput = this.form.querySelector(
          'input[name="selling_plan"], select[name="selling_plan"]',
        );
        if (sellingPlanInput?.value) item.selling_plan = sellingPlanInput.value;

        items.push(item);
      }

      if (this.toggleCrossSells) {
        items.push(...this.toggleCrossSells.getSelectedItems().items);
      }

      const giftingBundle = this.querySelector('gifting-bundle');
      if (giftingBundle && typeof giftingBundle.getSelectedGifts === 'function') {
        items.push(...giftingBundle.getSelectedGifts());
      }

      items = items.filter((item) => item?.id && (parseInt(item.quantity, 10) || 1) > 0);
      items = this.#mergeItemsById(items);

      if (items.length === 0) throw new Error('Aucun article valide à ajouter au panier.');

      const quantityAddedToCart = items.reduce(
        (total, item) => total + (parseInt(item.quantity, 10) || 1),
        0,
      );

      const sections = [];
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
        }),
      });

      const addPayload = await addResponse.json();
      if (!addResponse.ok) {
        throw new Error(addPayload.description || addPayload.message || 'Impossible d’ajouter au panier.');
      }

      if (backendOrderNote) {
        try {
          await this.#appendBackendOrderNote(backendOrderNote);
        } catch (noteError) {
          console.error('Erreur note Shopify:', noteError);
        }
      }

      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
      const newItemCount = currentItemCount != null
        ? currentItemCount + (quantityAddedToCart || 1)
        : undefined;

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'product-form',
            sections: addPayload.sections,
          },
        ),
      );

      if (this.addToCartBehavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: {
              type: 'success',
              message: Theme.translations.addToCartNotificationMessage,
            },
          }),
        );
      }
    } catch (error) {
      console.error('Erreur ajout panier:', error);
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: {
            type: 'error',
            message: error instanceof Error ? error.message : String(error),
          },
        }),
      );
    } finally {
      this.addToCartButton?.classList.remove('is-loading');
    }
  }

  #buildBackendOrderNote() {
    const flocageBundle = this.querySelector('flocage-bundle');
    if (!flocageBundle || typeof flocageBundle.buildBackendOrderNote !== 'function') return '';
    return flocageBundle.buildBackendOrderNote(this.quantityBreaks);
  }

  async #appendBackendOrderNote(note) {
    const root = window.Shopify?.routes?.root || '/';
    const cartResponse = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!cartResponse.ok) throw new Error('Impossible de lire la note du panier.');
    const cart = await cartResponse.json();
    const existingNote = cart.note?.trim() || '';
    const separator = existingNote ? '\n\n====================\n\n' : '';
    const updatedNote = `${existingNote}${separator}${note}`.trim();

    const updateResponse = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ note: updatedNote }),
    });

    if (!updateResponse.ok) {
      const payload = await updateResponse.json().catch(() => ({}));
      throw new Error(payload.description || payload.message || 'Impossible de mettre à jour la note Shopify.');
    }
  }

  /**
   * Fusionne uniquement les lignes réellement identiques.
   * Deux maillots ayant la même variante mais une position ou un flocage différent
   * restent séparés dans le panier Shopify.
   * @param {Array<{ id: string, quantity: number, properties?: Object, selling_plan?: string }>} items
   * @returns {Array<{ id: string, quantity: number, properties?: Object, selling_plan?: string }>}
   */
  #mergeItemsById(items) {
    const compatibleItems = new Map();

    for (const item of items) {
      const normalizedProperties = Object.entries(item.properties || {})
        .filter(([, value]) => value !== '' && value != null)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, value]) => `${key}=${String(value)}`)
        .join('&');

      const key = [String(item.id), item.selling_plan || '', normalizedProperties].join('::');

      if (!compatibleItems.has(key)) {
        compatibleItems.set(key, {
          ...item,
          properties: Object.keys(item.properties || {}).length > 0 ? { ...item.properties } : undefined,
          quantity: 0,
        });
      }

      const merged = compatibleItems.get(key);
      merged.quantity += parseInt(item.quantity, 10) || 1;
    }

    return Array.from(compatibleItems.values());
  }
  /**
   * Permet de récupérer les prix totaux du formulaire
   * (incluant le produit de base, sa quantité, les cross-sells à activer, l'abonnement, et les réductions sur la quantité)
   * @returns {Object} { price: number, compare_at_price: number, difference_price: number }
   */
  getCurrentTotalPrice() {
    // Le prix de base du produit est stocké dans le dataset du bouton d'ajout au panier
    let basePrice = parseInt(this.addToCartButtonContainer.dataset.price);
    let baseCompareAtPrice = basePrice;
    if (this.addToCartButtonContainer.dataset.compareAtPrice) {
      baseCompareAtPrice = parseInt(this.addToCartButtonContainer.dataset.compareAtPrice);
    }

    let newPrice = basePrice;
    let newCompareAtPrice = baseCompareAtPrice;
    let quantity = 1;

    if (this.quantityBreaks?.isActive?.()) {
      const prices = this.quantityBreaks.getPrices();
      const discount = this.quantityBreaks.getDiscount();
      const priceAfterQB = parseInt(prices.price);
      const compareAtPrice = parseInt(prices.compareAtPrice);

      // Récupérer la quantité sélectionnée
      const selectedQuantity = parseInt(this.quantityBreaks.getSelectedQuantity()?.dataset?.quantity) || 1;
      quantity = this.quantityBreaks.getSelectedItems().reduce((acc, item) => acc + item.quantity, 0);

      // Calculer le prix AVANT réduction QB (prix unitaire × quantité, comme dans subscription.js)
      const priceBeforeQB = basePrice * selectedQuantity;
      newCompareAtPrice = baseCompareAtPrice * selectedQuantity;

      // Récupérer les prix des cross-sells QB
      let crossSellPrice = 0;
      let crossSellCompareAtPrice = 0;
      if (typeof this.quantityBreaks.getCrossSellPrices === 'function') {
        const crossSellPrices = this.quantityBreaks.getCrossSellPrices();
        crossSellPrice = parseInt(crossSellPrices.price) || 0;
        crossSellCompareAtPrice = parseInt(crossSellPrices.compareAtPrice) || crossSellPrice;
      }

      if (this.subscription && this.subscription.isSubscriptionSelected()) {
        // Ordre Shopify : Abonnement → puis QB
        // 1. Appliquer la réduction abonnement sur le prix avant QB
        let priceWithAbo = this.subscription.calculateAdjustedPrice(priceBeforeQB, selectedQuantity);
        // 2. Appliquer la réduction QB
        if (discount.type === 'percentage' && discount.percentage > 0) {
          newPrice = Math.round((priceWithAbo * (100 - discount.percentage)) / 100);
        } else if (discount.value > 0) {
          newPrice = Math.max(0, priceWithAbo - discount.value);
        } else {
          newPrice = priceWithAbo;
        }
      } else {
        // Achat unique : prix après QB directement
        newPrice = priceAfterQB;
        newCompareAtPrice = compareAtPrice;
      }

      // Ajouter les cross-sells du quantity break (après les réductions, pas de réduction sur les cross-sells)
      newPrice += crossSellPrice;
      newCompareAtPrice += crossSellCompareAtPrice;
    } else {
      // Cas sans quantity breaks : récupérer la quantité du sélecteur ou 1 par défaut
      quantity = parseInt(this.quantitySelectorInput?.value) || 1;
      newPrice = newPrice * quantity;
      newCompareAtPrice = newCompareAtPrice * quantity;

      // Appliquer l'abonnement
      if (this.subscription && this.subscription.isSubscriptionSelected()) {
        newPrice = this.subscription.calculateAdjustedPrice(newPrice, quantity);
      }
    }

    // Gestion des cross-sells (ajoutés après l'abonnement car ils ont leur propre prix)
    if (this.toggleCrossSells && typeof this.toggleCrossSells.getSelectedItems === 'function') {
      newPrice += parseInt(this.toggleCrossSells.getSelectedItems().total_price);
      newCompareAtPrice += parseInt(this.toggleCrossSells.getSelectedItems().total_compare_at_price);
    }

    // S'assurer que le prix ne soit jamais négatif
    newPrice = Math.max(0, newPrice);
    const differencePrice = newCompareAtPrice - newPrice;

    return {
      price: newPrice,
      compare_at_price: newCompareAtPrice,
      difference_price: differencePrice,
    };
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}